<script setup lang="ts">
definePageMeta({
  alias: '/:language/deals/test/:slug',
})

const api = useApi()
const currentRoute = useCurrentRoute()
const mediaQuery = useMediaQuery()
const i18n = useI18n()
const tracking = useTracking()
const session = useSessionStorage()

const deal: Ref<Deal> = ref()
const deals: Ref<DealList> = ref([])
const reviews: Ref<ReviewList> = ref()
const reviewsMeta: Ref<{ [key: string]: any }> = ref(0)
const localOrder: Ref<LocalOrder> = ref()

const loadDeal = async () => {
  try {
    deal.value = (
      await api.deals.getBySlug(currentRoute.params.slug?.toString())
    ).data.value!

    if (!deal.value) {
      navigateTo(getRoute(''))
      return false
    }

    localOrder.value = useLocalOrder(deal.value, null, true)
    tracking.trackClick(deal.value)

    if (
      deal.value.canonical?.slug &&
      !isTest() &&
      !globalThis.location?.href.includes('redirect=') &&
      !globalThis.location?.href.includes('variant_id=')
    ) {
      navigateTo(
        getRoute(
          `deals/${deal.value.canonical?.slug}${globalThis.location.search}`,
        ),
      )
    }

    try {
      pushEcommerceEventToDataLayer(
        'view_item',
        parseFloat(deal.value.discountPrice),
        {
          items: [
            {
              item_id: deal.value.id,
              //id: deal.value.id,
              item_name: deal.value.name,
              price: parseFloat(deal.value.discountPrice),
              item_list_name: 'Product Page',
            },
          ],
        },
      )
    } catch (error) {}
  } catch (error) {
    console.error('Error loading deal:', error)
  }
}

const loadReviews = async () => {
  try {
    const response = await api.reviews.list(deal.value.id, 0, 100)
    const reviewsWithMeta = response.data.value

    const sortedReviews = reviewsWithMeta.data
      .sort((a, b) => {
        const aHasPhoto = a.photo && a.photo.length > 0 ? 1 : 0
        const bHasPhoto = b.photo && b.photo.length > 0 ? 1 : 0
        return bHasPhoto - aHasPhoto
      })
      .slice(0, 5)

    reviews.value = sortedReviews
    reviewsMeta.value = reviewsWithMeta.meta
  } catch (error) {
    console.error('Error loading reviews:', error)
  }
}

const loadOtherDeals = async () => {
  try {
    deals.value = (await api.deals.list()).data.value!
  } catch (error) {
    console.error('Error loading other deals:', error)
  }
}

const loadPageData = async () => {
  await loadDeal()
  loadReviews()
  loadOtherDeals()
}

const shouldShowPlaceholder = usePreloader(loadPageData)

const mainVariants = computed(() => {
  const focusOnVariant = parseInt(
    currentRoute.query?.variant_id?.toString() ?? '0',
  )

  return (
    deal.value?.variants?.filter((variant: Variant) => {
      // if we're not focussing on a variant, return true
      if (!focusOnVariant) {
        return true
      }

      // check if the variant's options include the variant we're focussing on
      let matchesFocusVariant = false

      variant.options.forEach((option: Option) => {
        matchesFocusVariant =
          matchesFocusVariant || option.id === focusOnVariant
      })

      return matchesFocusVariant
    }) ?? []
  )
})

const otherVariants = computed(() => {
  return deal.value?.variants?.filter((variant: Variant) => {
    return !mainVariants.value?.includes(variant)
  })
})

const extraVariants = computed(() => {
  return deal.value.extraVariants
    .map((item) => {
      // Create a shallow copy of the item with filtered options
      const filteredItem = {
        ...item,
        options: item.options.filter(
          (option) =>
            !(
              option.isFee === false &&
              option.isExtra === true &&
              option.isSingular === true
            ),
        ),
      }

      // Return the item only if it has valid options
      return filteredItem.options.length > 0 ? filteredItem : null
    })
    .filter(Boolean) // Remove null values
})

useHead({
  title: () =>
    deal.value ? stripTags(deal.value.shortName) + ' — Outspot' : 'Outspot',
  meta: () => [
    { property: 'og:image', content: deal.value?.images[0]?.xl },
    { property: 'og:title', content: stripTags(deal.value?.shortName) },
    {
      property: 'og:description',
      content: stripTags(deal.value?.description).substring(0, 200) + '…',
    },
    { name: 'description', content: stripTags(deal.value?.description) },
    { property: 'og:url', content: document.location.href },
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:url', content: document.location.href },
    { property: 'twitter:title', content: stripTags(deal.value?.shortName) },
    {
      property: 'twitter:description',
      content: stripTags(deal.value?.description).substring(0, 200) + '…',
    },
    { property: 'twitter:image', content: deal.value?.images[0]?.xl },
  ],
  script: () => [
    {
      type: 'application/ld+json',
      textContent: JSON.stringify({
        '@context': 'https://schema.org/',
        '@graph': (mainVariants.value ?? []).map((variant: Variant) => {
          return {
            '@type': 'Product',
            name: stripTags(deal.value?.name + ' — ' + variant.name),
            image: [
              variant.image?.xl,
              ...(deal.value?.images ?? []).map((image: Image) => image.xl),
            ].filter((imageSrc) => !!imageSrc),
            description: stripTags(deal.value?.description),
            offers: {
              '@type': 'Offer',
              priceCurrency: i18n.t('language.currency'),
              itemCondition: 'https:\/\/schema.org\/NewCondition',
              availability: 'https:\/\/schema.org\/InStock',
              price: variant.discountPrice,
              priceSpecification: [
                {
                  '@type': 'UnitPriceSpecification',
                  priceType: 'https:\/\/schema.org\/ListPrice',
                  price: variant.originalPrice,
                },
                {
                  '@type': 'UnitPriceSpecification',
                  priceType: 'https:\/\/schema.org\/SalePrice',
                  price: variant.discountPrice,
                  validFrom: deal.value?.startDate,
                  validThrough: deal.value?.endDate,
                },
              ],
            },
          }
        }),
      }),
    },
  ],
})

function optionSelected(option?: Option) {
  localOrder.value?.incrementInitialOption(option)

  try {
    pushEcommerceEventToDataLayer(
      'begin_checkout',
      parseFloat(deal.value.discountPrice),
      {
        items: [
          {
            item_id: deal.value.id,
            //id: deal.value.id,
            item_name: deal.value.name,
            price: parseFloat(deal.value.discountPrice),
            quantity: 1,
          },
        ],
      },
    )

    pushCustomEventToDataLayer('salesflow_step_1')
  } catch (error) {}

  navigateTo(getDealRoute(deal.value, 'order'))
}

const showAdvicePriceText = isLocale('nd')
</script>

<template>
  <os-page :show-placeholder="shouldShowPlaceholder">
    <os-column cross-axis-alignment="stretch">
      <os-row main-axis-alignment="space-between">
        <ion-button
          fill="clear"
          size="small"
          :router-link="getRoute(session.returnToDealsPage ?? '')"
          router-direction="back"
        >
          <ion-icon :icon="ioniconsChevronBackOutline" slot="start" />

          {{ $t('label.all_deals') }}
        </ion-button>
      </os-row>

      <template v-if="!mediaQuery.isLarge">
        <os-column cross-axis-alignment="start" gap="narrow">
          <os-row
            main-axis-alignment="space-between"
            cross-axis-alignment="start"
          >
            <h1 class="h1--black">
              {{ stripTags(deal.name) }}
            </h1>

            <os-share-button />
          </os-row>

          <os-stars
            :score="reviewsMeta.score"
            :reviews="reviewsMeta.total"
            :router-link="getDealRoute(deal, 'reviews')"
          />
        </os-column>
      </template>

      <os-column class="deal__countdown-and-sold" gap="narrow">
        <os-countdown :to="deal.endDate" hide-when-zero />

        <small
          v-if="deal.sold > 10"
          v-html="
            $t('deal.sold', { sold: '&nbsp;<b>' + deal.sold + '</b>&nbsp;' })
          "
        ></small>
      </os-column>

      <os-container
        v-if="mediaQuery.isLarge"
        class="vwo-container"
        id="vwo-deal-container"
      ></os-container>

      <os-wrap :columns="mediaQuery.isLarge ? 2 : 1">
        <os-column cross-axis-alignment="start">
          <os-slides zoom paginate>
            <!-- First image slide -->
            <os-slide
              v-if="deal.images && deal.images.length > 0"
              :uspTemplate="deal.images[0].uspTemplate"
            >
              <os-usp-image
                :imageSrc="deal.images[0].xl"
                :altText="deal.name"
                :uspTemplate="deal.images[0].uspTemplate"
              />
            </os-slide>
            <!--  block for the embed youtube video -->
            <os-slide v-if="deal.embeds[0]">
              <div class="video-container">
                <iframe
                  class="youtube-embed-container"
                  :src="deal.embeds[0]"
                  frameborder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen
                  ref="videoElement"
                ></iframe>
                <!-- fall back image  -->
                <div class="fallback-image-container" v-if="deal.images[0].xl">
                  <img
                    :src="deal.images[0].xl"
                    :alt="deal.name"
                    class="video-thumb"
                  />
                  <ion-icon
                    :icon="ioniconsPlayCircleOutline"
                    slot="icon-only"
                    class="play-button"
                  />
                </div>
              </div>
            </os-slide>
            <!-- end embed youtube -->
            <!-- Remaining images slide -->
            <template v-if="deal.images && deal.images.length > 1">
              <os-slide
                v-for="(image, index) in deal.images.slice(1)"
                :key="index"
                :uspTemplate="image.uspTemplate"
              >
                <os-usp-image
                  :imageSrc="image.xl"
                  :altText="deal.name"
                  :uspTemplate="image.uspTemplate"
                />
              </os-slide>
            </template>
          </os-slides>
          <template v-if="mediaQuery.isLarge">
            <div>
              <os-prose :content="deal.description" />

              <os-separator />

              <os-prose :content="deal.practical" />

              <os-separator />

              <template v-if="!Array.isArray(deal.map)">
                <div>
                  <os-leaf-map
                    :hasMap="true"
                    :lat="Number(deal.map.lat)"
                    :lng="Number(deal.map.long)"
                    :zoom="Number(deal.map.zoom)"
                    :title="deal.slug"
                  />
                </div>

                <os-separator />
              </template>

              <small
                >{{ $t('deal.campaign_number') }}: {{ deal.campaignId }}</small
              >
            </div>
          </template>
        </os-column>

        <os-column
          cross-axis-alignment="stretch"
          class="deal__details"
          :class="mediaQuery.isLarge ? 'deal__details--with-padding' : ''"
        >
          <template v-if="mediaQuery.isLarge">
            <os-column cross-axis-alignment="start" gap="narrow">
              <os-row
                main-axis-alignment="space-between"
                cross-axis-alignment="start"
              >
                <h1 class="h1--black">
                  {{ stripTags(deal.name) }}
                </h1>

                <os-share-button />
              </os-row>

              <os-stars
                :score="reviewsMeta.score"
                :reviews="reviewsMeta.total"
                :router-link="getDealRoute(deal, 'reviews')"
              />
            </os-column>
          </template>

          <os-row main-axis-alignment="space-between">
            <h2 class="h1">
              <os-currency :value="deal.discountPrice"></os-currency>
            </h2>

            <h3
              v-if="
                deal.originalPrice &&
                parseFloat(deal.originalPrice) > parseFloat(deal.discountPrice)
              "
            >
              <b
                ><s><os-currency :value="deal.originalPrice"></os-currency></s>
                {{ showAdvicePriceText ? $t('deal.advice_price') : '' }}</b
              >
            </h3>

            <os-expanded />

            <ion-button secondary inert v-if="deal.percentOff">
              <os-percent :value="deal.percentOff * -1" />
              {{ $t('label.discount') }}
            </ion-button>
          </os-row>

          <template v-if="!mediaQuery.isLarge">
            <h2 class="h1" @click="optionSelected()" v-if="false">
              {{ $t(deal.isOpenGds ? 'label.book' : 'label.order_now') }}
            </h2>

            <os-column
              cross-axis-alignment="stretch"
              :gap="mediaQuery.isMedium ? 'default' : 'narrow'"
            >
              <os-variant-card
                v-for="variant in mainVariants ?? []"
                :variant="variant"
                mode="buy"
                @select="optionSelected"
              />
            </os-column>

            <template v-if="otherVariants.length > 0">
              <h2>{{ $t('label.also_available') }}</h2>

              <os-column
                cross-axis-alignment="stretch"
                :gap="mediaQuery.isMedium ? 'default' : 'narrow'"
              >
                <os-variant-card
                  v-for="variant in otherVariants ?? []"
                  :variant="variant"
                  mode="buy"
                  @select="optionSelected"
                />
              </os-column>
            </template>

            <template v-if="extraVariants.length > 0">
              <h2>{{ $t('label.upselling_next_step') }}</h2>

              <os-column
                cross-axis-alignment="stretch"
                :gap="mediaQuery.isMedium ? 'default' : 'narrow'"
              >
                <os-variant-card
                  v-for="variant in extraVariants ?? []"
                  :variant="variant"
                  @select="optionSelected"
                />
              </os-column>
            </template>

            <os-separator />
          </template>

          <os-prose :content="deal.highlights" mode="usp" />

          <template v-if="!mediaQuery.isLarge">
            <os-separator />

            <os-prose :content="deal.description" />

            <os-separator />

            <os-prose :content="deal.practical" />

            <os-separator />

            <template v-if="!Array.isArray(deal.map)">
              <div>
                <os-leaf-map
                  :hasMap="true"
                  :lat="Number(deal.map.lat)"
                  :lng="Number(deal.map.long)"
                  :zoom="Number(deal.map.zoom)"
                  :title="deal.slug"
                />
              </div>
              <os-separator />
            </template>

            <small
              >{{ $t('deal.campaign_number') }}: {{ deal.campaignId }}</small
            >
          </template>

          <template v-if="mediaQuery.isLarge">
            <h2 class="h1 gutter-above" @click="optionSelected()">
              {{ $t(deal.isOpenGds ? 'label.book' : 'label.order_now') }}
            </h2>

            <os-column
              cross-axis-alignment="stretch"
              :gap="mediaQuery.isMedium ? 'default' : 'narrow'"
            >
              <os-variant-card
                v-for="variant in mainVariants ?? []"
                :variant="variant"
                mode="buy"
                @select="optionSelected"
              />
            </os-column>

            <template v-if="otherVariants.length > 0">
              <h2>{{ $t('label.also_available') }}</h2>

              <os-column
                cross-axis-alignment="stretch"
                :gap="mediaQuery.isMedium ? 'default' : 'narrow'"
              >
                <os-variant-card
                  v-for="variant in otherVariants ?? []"
                  :variant="variant"
                  mode="buy"
                  @select="optionSelected"
                />
              </os-column>
            </template>

            <template v-if="extraVariants.length > 0">
              <h2>{{ $t('label.upselling_next_step') }}</h2>

              <os-column
                cross-axis-alignment="stretch"
                :gap="mediaQuery.isMedium ? 'default' : 'narrow'"
              >
                <os-variant-card
                  v-for="variant in extraVariants ?? []"
                  :variant="variant"
                  @select="optionSelected"
                />
              </os-column>
            </template>
          </template>

          <template v-if="reviews?.length > 0">
            <os-separator />

            <template v-if="mediaQuery.isSmall">
              <os-row main-axis-alignment="space-between">
                <os-expanded>
                  <h2 class="h1">{{ $t('label.reviews') }}</h2>
                </os-expanded>

                <os-stars :score="reviewsMeta.score" size="small" />
              </os-row>
            </template>

            <template v-else>
              <os-column gap="narrow" cross-axis-alignment="start">
                <h2 class="h1">{{ $t('label.reviews') }}</h2>

                <os-stars :score="reviewsMeta.score" size="small" />
              </os-column>
            </template>

            <os-column
              cross-axis-alignment="stretch"
              :gap="mediaQuery.isMedium ? 'default' : 'narrow'"
            >
              <os-review-card
                v-for="review in reviews"
                :key="review.id"
                :review="review"
                :isReviewPage="false"
              />
            </os-column>

            <ion-button
              fill="clear"
              :router-link="getDealRoute(deal, 'reviews')"
              router-direction="forward"
              v-if="reviewsMeta.total > 5"
            >
              <ion-icon :icon="ioniconsChevronForwardOutline" slot="end" />

              {{ $t('label.show_all_reviews') }}
            </ion-button>
          </template>
        </os-column>
      </os-wrap>
    </os-column>

    <os-column cross-axis-alignment="start">
      <os-line class="gutter-above" mode="end">
        <h2 class="h1">{{ $t('label.other_deals') }}</h2>
      </os-line>

      <os-wrap
        :columns="mediaQuery.isLarge ? 2 : 1"
        :gap="mediaQuery.isMedium ? 'default' : 'narrow'"
      >
        <template v-for="(sectionDeals, sectionName) in deals">
          <template v-for="otherDeal in sectionDeals">
            <os-deal-card
              v-if="otherDeal.id !== deal.id"
              :key="otherDeal.id"
              :deal="otherDeal"
              :display-countdown="true"
            />
          </template>
        </template>
      </os-wrap>
    </os-column>

    <template #footer>
      <os-deal-footer :deal="deal" @select="optionSelected" />
    </template>

    <template #placeholder>
      <os-column cross-axis-alignment="stretch">
        <os-skeleton-button />

        <os-column class="gutter-above">
          <os-skeleton-h1 />
          <os-skeleton-h2 />
        </os-column>

        <os-wrap class="gutter-above" :columns="mediaQuery.isLarge ? 2 : 1">
          <os-column cross-axis-alignment="stretch">
            <os-skeleton-image />

            <os-column cross-axis-alignment="stretch">
              <os-skeleton-h2 />
              <os-skeleton-p class="gutter-above" />
              <os-skeleton-p />
              <os-skeleton-p class="gutter-above" />
              <os-skeleton-p />
              <os-skeleton-p class="gutter-above" />
              <os-skeleton-p />
              <os-skeleton-p />
              <os-skeleton-p class="gutter-above" />
              <os-skeleton-p />
              <os-skeleton-p class="gutter-above" />
            </os-column>
          </os-column>

          <os-column cross-axis-alignment="stretch">
            <os-skeleton-h2 />
            <os-skeleton-p class="gutter-above" />
            <os-skeleton-p />
            <os-skeleton-p />
            <os-skeleton-p />
            <os-skeleton-p />
            <os-skeleton-p />
            <os-skeleton-p />
            <os-skeleton-card class="gutter-above" />
            <os-skeleton-card />
            <os-skeleton-card />
            <os-skeleton-card />
            <os-skeleton-card />
            <os-skeleton-h2 class="gutter-above" />
            <os-skeleton-card class="gutter-above" />
            <os-skeleton-card />
            <os-skeleton-card />
            <os-skeleton-card />
            <os-skeleton-card />
          </os-column>
        </os-wrap>
      </os-column>
    </template>

  </os-page>
</template>

<style scoped>
.deal__countdown-and-sold {
  padding: 1rem;
  background-color: var(--background--primary);
  color: var(--header);
  width: 100%;
}

.deal__details.deal__details--with-padding {
  padding: 1rem;
}

.video-container {
  width: 100%;
  height: 0;
  padding-bottom: 66.65%;
  position: relative;
}

.video-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.video-thumb {
  display: none;
}

.swiper-thumbs .youtube-embed-container {
  display: none;
}

.swiper-thumbs .video-thumb {
  display: block;
}

.swiper-thumbs .play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 55px;
  color: var(--accent);
  cursor: pointer;
  pointer-events: none !important;
}
</style>
